import type { CardExpandableResolved } from './types';
import { Collapsible } from '@/components/Collapsible';
import { SlotRenderer } from '@/components/SlotRenderer';
import { Text } from '@/lib/render/Text';

/**
 * A labelled panel inside a Flexible Content Card that expands to reveal a
 * Content Snippet — the replacement for the legacy `panel panel-transparent
 * panel-accordion` blocks (SH-419).
 *
 * It uses {@link Collapsible} rather than the Accordion group: a single
 * self-contained disclosure owns its own state and needs no cross-item
 * coordination. The chrome overrides mirror the Collapsible Widget's, so the
 * panel reads as transparent inside a card that already brings its own
 * background instead of nesting a second filled panel.
 */
export function CardExpandableComponent({
  resolved,
}: {
  resolved: CardExpandableResolved;
}) {
  const { label, snippet } = resolved;

  // A widget that finds no data hides itself (concept §3.4). An empty block
  // list counts as no data: a dangling relation, or a snippet whose body was
  // cleared, must not leave a panel that opens onto nothing.
  if (!label || !snippet || snippet.blocks.length === 0) {
    return null;
  }

  return (
    <Collapsible
      // Brand primary blue and bold, so the caption reads as the card's own
      // affordance rather than body copy. `centered` both centres it, matching
      // the rest of the card, and keeps the chevron beside the caption instead
      // of pushing it out to the card's right edge, where it looks unrelated
      // to the caption it belongs to.
      header={
        <Text field="label" className="text-primary font-bold">
          {label}
        </Text>
      }
      centered
      className="w-full rounded-none bg-transparent px-0"
      contentClassName=""
    >
      <SlotRenderer blocks={snippet.blocks} zoneName="card-expandable-body" />
    </Collapsible>
  );
}
